import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  streamTurn,
  type Attachment,
  type Conversation,
  type LiveEvent,
  type Message,
} from "./api";
import { applyBlockEvent, settleRunningTools } from "./blocks";

export type ChatState = {
  conversation: Conversation | null;
  messages: Message[];
  /** True while a turn is streaming — drives Stop vs Send. */
  busy: boolean;
  /** SSE transport health, so the UI can say "reconnecting" honestly. */
  connected: boolean;
  /** True once the transport has been down for ~3s — shown in the thread. */
  reconnecting: boolean;
  loading: boolean;
  error: string;
};

const EMPTY: ChatState = {
  conversation: null,
  messages: [],
  busy: false,
  connected: true,
  reconnecting: false,
  loading: false,
  error: "",
};

function blankAssistant(convId: string, id: string, seq: number): Message {
  return {
    id,
    convId,
    seq,
    role: "assistant",
    content: "",
    thinking: "",
    status: "streaming",
    error: "",
    engine: "",
    model: "",
    usage: null,
    createdAt: Date.now(),
    tools: [],
    attachments: [],
    blocks: [],
  };
}

/**
 * Owns one conversation's state.
 *
 * Deltas are applied to a single message object rather than re-rendering the
 * whole thread. The old build called `resetThread()` and rebuilt every bubble
 * from scratch on each poll, which is why it flickered and lost scroll.
 */
export function useConversation(convId: string | null) {
  const [state, setState] = useState<ChatState>(EMPTY);
  const since = useRef(-1);
  const unsub = useRef<(() => void) | null>(null);
  const activeMsg = useRef<string>("");

  const patchMessage = useCallback((msgId: string, fn: (m: Message) => Message) => {
    setState((s) => {
      const i = s.messages.findIndex((m) => m.id === msgId);
      if (i === -1) return s;
      const next = s.messages.slice();
      next[i] = fn(next[i]!);
      return { ...s, messages: next };
    });
  }, []);

  const applyEvent = useCallback(
    (e: LiveEvent) => {
      since.current = e.i;
      const msgId = e.msgId;
      const evt = e.evt;

      setState((s) => {
        // A turn started by another device: materialise its bubble.
        if (!s.messages.some((m) => m.id === msgId)) {
          const seq = (s.messages.at(-1)?.seq ?? 0) + 1;
          return {
            ...s,
            busy: true,
            messages: [...s.messages, blankAssistant(s.conversation?.id ?? "", msgId, seq)],
          };
        }
        return s;
      });

      switch (evt.type) {
        case "start":
          activeMsg.current = msgId;
          patchMessage(msgId, (m) => ({ ...m, model: evt.model, status: "streaming" }));
          break;

        case "text":
          patchMessage(msgId, (m) => ({
            ...m,
            content: m.content + evt.delta,
            blocks: applyBlockEvent(m.blocks ?? [], evt),
          }));
          break;

        case "thinking":
          patchMessage(msgId, (m) => ({
            ...m,
            thinking: m.thinking + evt.delta,
            blocks: applyBlockEvent(m.blocks ?? [], evt),
          }));
          break;

        case "tool_start":
          patchMessage(msgId, (m) =>
            m.tools.some((t) => t.toolId === evt.id)
              ? { ...m, blocks: applyBlockEvent(m.blocks ?? [], evt) }
              : {
                  ...m,
                  tools: [
                    ...m.tools,
                    {
                      id: evt.id,
                      toolId: evt.id,
                      name: evt.name,
                      status: "running",
                      input: evt.input,
                      output: "",
                    },
                  ],
                  blocks: applyBlockEvent(m.blocks ?? [], evt),
                },
          );
          break;

        case "tool_input":
          patchMessage(msgId, (m) => ({
            ...m,
            tools: m.tools.map((t) => (t.toolId === evt.id ? { ...t, input: evt.input } : t)),
            blocks: applyBlockEvent(m.blocks ?? [], evt),
          }));
          break;

        case "tool_end":
          patchMessage(msgId, (m) => ({
            ...m,
            tools: m.tools.map((t) =>
              t.toolId === evt.id
                ? { ...t, status: evt.ok ? "ok" : "failed", output: evt.output ?? t.output }
                : t,
            ),
            blocks: applyBlockEvent(m.blocks ?? [], evt),
          }));
          break;

        case "error":
          patchMessage(msgId, (m) => ({
            ...m,
            error: evt.message,
            status: evt.message.toLowerCase().includes("interrupted")
              ? "interrupted"
              : "error",
            blocks: settleRunningTools(m.blocks ?? []),
            tools: m.tools.map((t) => (t.status === "running" ? { ...t, status: "failed" } : t)),
          }));
          break;

        case "retry":
          patchMessage(msgId, (m) => ({ ...m, status: "streaming", error: "" }));
          break;

        case "done":
          patchMessage(msgId, (m) => ({
            ...m,
            status:
              m.status === "error"
                ? "error"
                : m.status === "interrupted"
                  ? "interrupted"
                  : evt.stopReason === "stopped"
                    ? "stopped"
                    : evt.stopReason === "interrupted"
                      ? "interrupted"
                      : "complete",
            usage: evt.usage ?? m.usage,
            blocks: settleRunningTools(m.blocks ?? []),
            tools: m.tools.map((t) => (t.status === "running" ? { ...t, status: "failed" } : t)),
          }));
          setState((s) => ({ ...s, busy: false }));
          activeMsg.current = "";
          break;

        default:
          break;
      }
    },
    [patchMessage],
  );

  const attach = useCallback(
    (id: string) => {
      unsub.current?.();
      unsub.current = streamTurn(id, () => since.current, {
        onEvent: applyEvent,
        onIdle: () => setState((s) => ({ ...s, busy: false })),
        onStatus: (connected) =>
          setState((s) => ({
            ...s,
            connected,
            reconnecting: connected ? false : s.reconnecting,
          })),
      });
    },
    [applyEvent],
  );

  // Load (or reload) the conversation whenever the route id changes.
  useEffect(() => {
    unsub.current?.();
    unsub.current = null;
    since.current = -1;
    activeMsg.current = "";

    if (!convId) {
      setState(EMPTY);
      return;
    }

    let cancelled = false;
    setState({ ...EMPTY, loading: true });

    api
      .conversation(convId)
      .then(({ conversation, messages, live }) => {
        if (cancelled) return;
        // While a turn is live, the persisted text lags behind by up to one
        // flush interval. Replay the event log from zero instead of trusting it.
        const hydrated = live
          ? messages.map((m) =>
              m.id === live.msgId ? { ...m, content: "", thinking: "", tools: [], blocks: [] } : m,
            )
          : messages;
        setState({
          conversation,
          messages: hydrated,
          busy: Boolean(live),
          connected: true,
          reconnecting: false,
          loading: false,
          error: "",
        });
        attach(convId);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState({ ...EMPTY, error: e instanceof Error ? e.message : String(e) });
      });

    return () => {
      cancelled = true;
      unsub.current?.();
      unsub.current = null;
    };
  }, [convId, attach]);

  useEffect(() => {
    if (state.connected) {
      setState((s) => (s.reconnecting ? { ...s, reconnecting: false } : s));
      return;
    }
    const t = window.setTimeout(() => {
      setState((s) => (s.connected ? s : { ...s, reconnecting: true }));
    }, 3000);
    return () => window.clearTimeout(t);
  }, [state.connected]);

  const send = useCallback(
    async (opts: {
      message: string;
      attachments?: Attachment[];
      engine?: string;
      model?: string;
      retryFromSeq?: number;
      buildMode?: boolean;
    }): Promise<string> => {
      const targetConv = convId ?? undefined;

      // Optimistic: show the user's message immediately. A phone on wifi should
      // never appear to swallow a tap while the POST is in flight.
      const optimisticId = `local-${Date.now()}`;
      setState((s) => {
        const kept =
          opts.retryFromSeq === undefined
            ? s.messages
            : s.messages.filter((m) => m.seq < opts.retryFromSeq!);
        return {
          ...s,
          busy: true,
          error: "",
          messages: [
            ...kept,
            {
              ...blankAssistant(targetConv ?? "", optimisticId, (kept.at(-1)?.seq ?? 0) + 1),
              role: "user",
              content: opts.message,
              status: "complete",
              attachments: opts.attachments ?? [],
            },
          ],
        };
      });

      try {
        const { convId: id } = await api.send({
          convId: targetConv,
          message: opts.message,
          engine: opts.engine,
          model: opts.model,
          attachments: opts.attachments,
          retryFromSeq: opts.retryFromSeq,
          buildMode: opts.buildMode,
        });

        // Reconcile against the server's authoritative ids, then stream.
        const fresh = await api.conversation(id);
        since.current = -1;
        setState({
          conversation: fresh.conversation,
          messages: fresh.live
            ? fresh.messages.map((m) =>
                m.id === fresh.live!.msgId
                  ? { ...m, content: "", thinking: "", tools: [], blocks: [] }
                  : m,
              )
            : fresh.messages,
          busy: Boolean(fresh.live),
          connected: true,
          reconnecting: false,
          loading: false,
          error: "",
        });
        attach(id);
        return id;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setState((s) => ({
          ...s,
          busy: false,
          error: msg,
          messages: s.messages.filter((m) => m.id !== optimisticId),
        }));
        throw e;
      }
    },
    [convId, attach],
  );

  const stop = useCallback(async () => {
    if (!convId) return;
    try {
      await api.stop(convId);
    } catch {
      // The turn may have finished between render and tap. Not an error.
    }
  }, [convId]);

  const retryFrom = useCallback(
    async (seq: number, message: string) => {
      await send({ message, retryFromSeq: seq });
    },
    [send],
  );

  return { ...state, send, stop, retryFrom };
}
